import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { ArrowLeft, Printer, Trophy, TrendingUp } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function PlayerReport() {
  const { playerId } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const token = localStorage.getItem('session_token');
    fetch(`${API_URL}/api/stats/players/${playerId}/report`, {
      headers: { 'Authorization': `Bearer ${token}` }
    })
      .then(r => r.json()).then(setData)
      .catch(console.error)
      .finally(() => setLoading(false));
  }, [playerId]);

  if (loading) return <div className="min-h-screen bg-slate-50 flex items-center justify-center"><div className="w-8 h-8 border-4 border-[#00247D] border-t-transparent rounded-full animate-spin" /></div>;
  if (!data) return <div className="p-8 text-center text-slate-500">Player not found</div>;

  const { player, games_played, season_totals, averages, per, career_highs, game_log } = data;

  return (
    <div className="min-h-screen bg-slate-50">
      <style>{`
        @page { size: portrait; margin: 1cm; }
        @media print {
          .no-print { display: none !important; }
          body { -webkit-print-color-adjust: exact !important; print-color-adjust: exact !important; }
        }
      `}</style>

      <header className="glass-effect sticky top-0 z-50 border-b border-slate-200 no-print">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 h-16 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button variant="ghost" onClick={() => navigate(-1)}><ArrowLeft className="w-5 h-5" /></Button>
            <h1 className="text-xl font-bold text-[#00247D]" style={{ fontFamily: 'Barlow Condensed' }}>PLAYER REPORT</h1>
          </div>
          <Button variant="outline" onClick={() => window.print()} data-testid="print-btn">
            <Printer className="w-4 h-4 mr-2" /> Print
          </Button>
        </div>
      </header>

      <div className="max-w-4xl mx-auto px-4 sm:px-6 py-8" id="report-content">
        {/* Player Header */}
        <Card className="mb-6 overflow-hidden" data-testid="player-header">
          <div className="bg-[#00247D] p-6 flex items-center gap-5">
            <div className="w-20 h-20 rounded-full bg-white text-[#00247D] flex items-center justify-center text-3xl font-black shrink-0">
              #{player.jersey}
            </div>
            <div className="text-white">
              <h2 className="text-3xl font-black uppercase" style={{ fontFamily: 'Barlow Condensed' }}>{player.name}</h2>
              <p className="text-white/70 text-sm">{player.team_name} &middot; {games_played} Games Played &middot; PER: {per}</p>
            </div>
          </div>
        </Card>

        {/* Season Averages */}
        <div className="grid grid-cols-5 sm:grid-cols-10 gap-2 mb-6" data-testid="season-averages">
          {[
            { label: 'PPG', val: averages.pts },
            { label: 'RPG', val: averages.reb },
            { label: 'APG', val: averages.ast },
            { label: 'SPG', val: averages.stl },
            { label: 'BPG', val: averages.blk },
            { label: 'TOPG', val: averages.to },
            { label: 'FG%', val: season_totals.fg_pct },
            { label: '3P%', val: season_totals.three_pct },
            { label: 'FT%', val: season_totals.ft_pct },
            { label: 'PER', val: per },
          ].map(s => (
            <Card key={s.label} className="text-center">
              <CardContent className="p-3">
                <div className="text-xl font-black text-[#00247D]">{s.val || 0}</div>
                <div className="text-[10px] font-bold text-slate-400 uppercase">{s.label}</div>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Career Highs */}
        {Object.keys(career_highs).length > 0 && (
          <Card className="mb-6">
            <CardContent className="p-4">
              <h3 className="text-sm font-bold text-[#00247D] uppercase mb-3 flex items-center gap-2" style={{ fontFamily: 'Barlow Condensed' }}>
                <Trophy className="w-4 h-4 text-amber-500" /> Season Highs
              </h3>
              <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
                {Object.entries(career_highs).map(([stat, info]) => (
                  <div key={stat} className="bg-amber-50 rounded-lg p-3 text-center">
                    <div className="text-2xl font-black text-amber-600">{info.value}</div>
                    <div className="text-[10px] font-bold text-slate-500 uppercase">{stat}</div>
                    <div className="text-[10px] text-slate-400 mt-1 truncate">{info.date}</div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Game Log */}
        <Card data-testid="game-log">
          <CardContent className="p-0">
            <div className="bg-[#00247D] px-4 py-3 flex items-center gap-2">
              <TrendingUp className="w-4 h-4 text-white" />
              <h3 className="text-white font-bold uppercase text-sm" style={{ fontFamily: 'Barlow Condensed' }}>Game Log</h3>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-left">
                <thead>
                  <tr className="bg-slate-50 border-b text-[10px] font-bold text-slate-500 uppercase">
                    <th className="px-3 py-2">Date</th>
                    <th className="px-3 py-2">Opponent</th>
                    <th className="px-3 py-2 text-center">PTS</th>
                    <th className="px-3 py-2 text-center">REB</th>
                    <th className="px-3 py-2 text-center">AST</th>
                    <th className="px-3 py-2 text-center">STL</th>
                    <th className="px-3 py-2 text-center">BLK</th>
                    <th className="px-3 py-2 text-center">TO</th>
                    <th className="px-3 py-2 text-center">FG%</th>
                    <th className="px-3 py-2 text-center">3P%</th>
                  </tr>
                </thead>
                <tbody>
                  {game_log.map((g, i) => (
                    <tr key={i} className={`border-b border-slate-100 ${i % 2 === 0 ? '' : 'bg-slate-50/50'}`}>
                      <td className="px-3 py-2 text-xs text-slate-500">{g.date}</td>
                      <td className="px-3 py-2 text-sm font-medium text-slate-700">vs {g.opponent}</td>
                      <td className="px-3 py-2 text-sm text-center font-bold text-[#00247D]">{g.pts}</td>
                      <td className="px-3 py-2 text-sm text-center">{g.reb}</td>
                      <td className="px-3 py-2 text-sm text-center">{g.ast}</td>
                      <td className="px-3 py-2 text-sm text-center">{g.stl}</td>
                      <td className="px-3 py-2 text-sm text-center">{g.blk}</td>
                      <td className="px-3 py-2 text-sm text-center text-red-500">{g.to}</td>
                      <td className="px-3 py-2 text-sm text-center">{g.fg_pct}%</td>
                      <td className="px-3 py-2 text-sm text-center">{g.three_pct}%</td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr className="bg-slate-100 font-bold text-sm">
                    <td className="px-3 py-2" colSpan={2}>TOTALS ({games_played} GP)</td>
                    <td className="px-3 py-2 text-center text-[#00247D]">{season_totals.pts}</td>
                    <td className="px-3 py-2 text-center">{season_totals.reb}</td>
                    <td className="px-3 py-2 text-center">{season_totals.ast}</td>
                    <td className="px-3 py-2 text-center">{season_totals.stl}</td>
                    <td className="px-3 py-2 text-center">{season_totals.blk}</td>
                    <td className="px-3 py-2 text-center text-red-500">{season_totals.to}</td>
                    <td className="px-3 py-2 text-center">{season_totals.fg_pct}%</td>
                    <td className="px-3 py-2 text-center">{season_totals.three_pct}%</td>
                  </tr>
                </tfoot>
              </table>
            </div>
          </CardContent>
        </Card>

        <div className="text-center mt-6 text-xs text-slate-400">
          scheduleguru.ws
        </div>
      </div>
    </div>
  );
}
